import {VnodeDOM} from 'mithril';
import app from 'flarum/forum/app';
import Button from 'flarum/common/components/Button';
import Modal, {IInternalModalAttrs} from 'flarum/common/components/Modal';
import Tooltip from 'flarum/common/components/Tooltip';
import classList from 'flarum/common/utils/classList';
import Picture, {StickerPlacement} from '../models/Picture';
import rgbValuesToHexString from '../utils/rgbValuesToHexString';
import {Sticker} from '../../common/json-settings';
import translate from '../../common/utils/translate';

interface StickersModalAttrs extends IInternalModalAttrs {
    picture: Picture
    onhide?: () => void
}

const DEFAULT_OPTIONS: fabric.IObjectOptions = {
    originX: 'center',
    originY: 'center',
    lockScalingFlip: true,
    stroke: 'transparent',
    strokeWidth: 5,
}

interface ObjectCustomProperties {
    isCustomColor?: boolean
    stickerKey?: string
}

async function loadStickerImage(url: string): Promise<HTMLImageElement> {
    return await (new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => {
            resolve(image);
        };
        image.onerror = reject;
        image.src = url;
        image.alt = 'Sticker';
    }));
}

export default class StickersModal extends Modal<StickersModalAttrs> {
    canvas!: fabric.Canvas
    observer!: ResizeObserver
    image?: HTMLImageElement
    colorPickerContext!: CanvasRenderingContext2D
    dirty: boolean = false
    dragInfo: {
        isDragging: boolean
        lastPosX: number
        lastPosY: number
    } = {
        isDragging: false,
        lastPosX: 0,
        lastPosY: 0,
    }
    canvasImage!: fabric.Image
    cropShape!: fabric.Rect
    cropShadow!: fabric.Rect

    className() {
        return 'CimaiseStickerComposer Modal--large';
    }

    title() {
        return 'Image editor';
    }

    oncreate(vnode: VnodeDOM<StickersModalAttrs>) {
        super.oncreate(vnode);

        const colorPickerCanvas = document.createElement('canvas');
        colorPickerCanvas.width = 1;
        colorPickerCanvas.height = 1;
        this.colorPickerContext = colorPickerCanvas.getContext('2d')!;

        this.canvas = new fabric.Canvas(vnode.dom.querySelector('canvas'), {
            // Disable multi-selection via mouse drag
            selection: false,
            defaultCursor: 'move',
        });

        this.canvas.on('mouse:wheel', opt => {
            const event = opt.e;
            const delta = event.deltaY;
            let zoom = this.canvas.getZoom();
            zoom *= 0.999 ** delta;
            if (zoom > 2) zoom = 2;
            if (zoom < 0.01) zoom = 0.01;
            this.canvas.zoomToPoint({x: event.offsetX, y: event.offsetY}, zoom);
            event.preventDefault();
            event.stopPropagation();

            const zoomLabel = vnode.dom.querySelector('.js-sticker-editor-zoom');

            if (zoomLabel) {
                zoomLabel.textContent = Math.round(zoom * 100) + '%';
            }
        });

        this.canvas.on('mouse:down', opt => {
            if (this.canvas.getActiveObject()) {
                // If something is selected, don't move around in the canvas
                return;
            }

            const event = opt.e;

            this.dragInfo.isDragging = true;
            this.dragInfo.lastPosX = event.clientX;
            this.dragInfo.lastPosY = event.clientY;
        });
        this.canvas.on('mouse:move', opt => {
            const event = opt.e;

            if (this.dragInfo.isDragging) {
                const vpt = this.canvas.viewportTransform!;
                vpt[4] += event.clientX - this.dragInfo.lastPosX;
                vpt[5] += event.clientY - this.dragInfo.lastPosY;
                this.canvas.requestRenderAll();
                this.dragInfo.lastPosX = event.clientX;
                this.dragInfo.lastPosY = event.clientY;
            }

            const active = this.canvas.getActiveObject();

            if (this.isStickerOrMask(active)) {
                this.updateShapeColor(active);
            }
        });
        this.canvas.on('mouse:up', opt => {
            // on mouse up we want to recalculate new interaction
            // for all objects, so we call setViewportTransform
            this.canvas.setViewportTransform(this.canvas.viewportTransform!);
            this.dragInfo.isDragging = false;
        });

        this.canvas.on('object:modified', () => {
            this.dirty = true;
            m.redraw();
        });

        this.canvas.on('object:scaling', event => {
            const minSize = app.forum.attribute<number>('cimaiseStickerMinSize') || 1;

            const object = event.target;

            if (object.width * object.scaleX < minSize) {
                const newHorizontalScale = minSize / object.width;

                const setScale: any = {
                    scaleX: newHorizontalScale,
                };

                if (!(object instanceof fabric.Rect)) {
                    // Apply same scale vertically to other shapes since we enforce fixed ratio
                    setScale.scaleY = newHorizontalScale;
                }

                object.set(setScale);
            }

            if (object instanceof fabric.Rect && ((object.height * object.scaleY) < minSize)) {
                object.set({
                    scaleY: minSize / object.height,
                });
            }
        });

        // We need a redraw because we show which element is selected in the list
        this.canvas.on('selection:created', () => {
            m.redraw();
        });
        this.canvas.on('selection:cleared', () => {
            m.redraw();
        });
        // We don't allow multi-selections but changing from one element to another will cause an update still
        this.canvas.on('selection:updated', () => {
            m.redraw();
        });

        const cropClipPath = new fabric.Rect({
            left: 0,
            top: 0,
            width: 0,
            height: 0,
            inverted: true,
            absolutePositioned: true,
        });

        this.cropShadow = new fabric.Rect({
            left: 0,
            top: 0,
            fill: 'rgba(0,0,0,0.5)',
            width: 8000,
            height: 6000,
            clipPath: cropClipPath,
            selectable: false,
        });

        this.canvas.add(this.cropShadow);

        this.cropShape = new fabric.Rect({
            left: 0,
            top: 0,
            width: 0,
            height: 0,
            fill: 'transparent',
            lockRotation: true,
            lockScalingFlip: true,
            selectable: false,
        });

        // Prevent infinite loop in the event listener
        let cropShapeAutoUpdate = false;

        this.cropShape.on('moving', () => {
            if (cropShapeAutoUpdate) {
                return;
            }

            cropShapeAutoUpdate = true;

            // Prevent moving selection out of the area
            this.cropShape.set({
                top: Math.max(0, Math.min(this.cropShape.top!, this.cropShadow.height! - this.cropShape.height!)),
                left: Math.max(0, Math.min(this.cropShape.left!, this.cropShadow.width! - this.cropShape.width!)),
            });

            cropShapeAutoUpdate = false;
        });

        this.cropShape.on('moved', () => {
            if (cropShapeAutoUpdate) {
                return;
            }

            cropShapeAutoUpdate = true;

            // The backend will case those numbers to int anyway but rounding up makes sure the frontend shows the correct crop alignement
            this.cropShape.set({
                top: Math.floor(this.cropShape.top!),
                left: Math.floor(this.cropShape.left!),
            });

            cropShapeAutoUpdate = false;
        });

        this.cropShape.on('scaled', () => {
            if (cropShapeAutoUpdate) {
                return;
            }

            cropShapeAutoUpdate = true;

            // We don't want to scale the shape, because this changes the size of the border as well
            // And this way the raw value can be used for the crop values during save
            this.cropShape.set({
                width: Math.floor(Math.min(this.cropShape.width! * this.cropShape.scaleX!, this.cropShadow.width! - this.cropShape.left!)),
                height: Math.floor(Math.min(this.cropShape.height! * this.cropShape.scaleY!, this.cropShadow.height! - this.cropShape.top!)),
                scaleX: 1,
                scaleY: 1,
            });

            cropShapeAutoUpdate = false;
        });

        this.cropShape.on('modified', () => {
            this.dirty = true;
            m.redraw();
        });

        this.cropShape.on('moving', this.resizeCropClipPath.bind(this));
        this.cropShape.on('scaling', this.resizeCropClipPath.bind(this));

        this.canvas.add(this.cropShape);

        const initialCrop = this.attrs.picture.crop();

        if (initialCrop) {
            this.cropShape.set(initialCrop);
            this.resizeCropClipPath();
        }

        this.fromStickers(this.attrs.picture.editStickers() || []);

        const editor = vnode.dom.querySelector('.js-sticker-editor')!;

        const {original} = this.attrs.picture.imageUrls();

        if (original) {
            this.image = new Image();
            this.image.src = original[0];
            this.image.onload = () => {
                this.canvasImage = new fabric.Image(this.image!);

                this.canvas.setBackgroundImage(this.canvasImage, this.canvas.renderAll.bind(this.canvas));
            };

            this.zoomToFit();

            this.cropShadow.set({
                width: original[1],
                height: original[2],
            });

            if (this.cropShape.width === 0) {
                this.cropShape.set({
                    width: original[1],
                    height: original[2],
                });

                this.resizeCropClipPath();
            }
        } else {
            console.warn('No original image file could be loaded');
        }

        this.observer = new ResizeObserver((entries) => {
            for (const entry of entries) {
                if (entry.contentRect) {
                    const {width} = entry.contentRect;

                    if (!width) {
                        console.warn('Modal was resized, editor width is zero, ignoring');

                        return;
                    }

                    console.info('Modal was resized, setting canvas width to ' + width);

                    this.canvas.setWidth(width);
                }
            }
        });

        this.observer.observe(editor);
    }

    onremove(vnode: VnodeDOM<StickersModalAttrs, this>) {
        super.onremove(vnode);

        this.observer.disconnect();
    }

    zoomToFit() {
        const {original} = this.attrs.picture.imageUrls();

        if (!original) {
            return;
        }

        const editor = this.element.querySelector('.js-sticker-editor')!;

        let zoomToFitTries = 1;
        let lastObservedWidth = 0;

        // We need to attempt the zoom multiple times because the editor doesn't always have a computed width ready
        const zoomToFitEditor = () => {
            if (zoomToFitTries > 10) {
                console.warn('Could not set default zoom in editor, editor has not rendered yet');

                return;
            }

            const {width} = editor.getBoundingClientRect();

            // Wait until we see the same non-zero width twice
            // Otherwise we get the wrong width as the modal animates its opening
            if (!width || width !== lastObservedWidth) {
                zoomToFitTries++;
                lastObservedWidth = width;

                setTimeout(zoomToFitEditor, 50);

                return;
            }

            const zoomToFit = width / original[1];

            if (zoomToFit < 1) {
                this.canvas.setZoom(zoomToFit);
            } else {
                console.info('No need to zoom, image is smaller than viewport');
            }
        };

        zoomToFitEditor();
    }

    resizeCropClipPath(): void {
        this.cropShadow.clipPath!.set({
            top: this.cropShape.top,
            left: this.cropShape.left,
            width: this.cropShape.width,
            height: this.cropShape.height,
            scaleX: this.cropShape.scaleX,
            scaleY: this.cropShape.scaleY,
        });
    };

    updateShapeColor(shape: fabric.Object & ObjectCustomProperties): void {
        if (!this.image || shape.isCustomColor || shape instanceof fabric.Image) {
            return;
        }

        const radius = Math.floor(shape instanceof fabric.Circle ? shape.radius! / 1.4142 : Math.min(shape.width!, shape.height!) / 2);

        // Cut a square of given "radius" and scale it down to 1x1
        this.colorPickerContext.drawImage(this.image, Math.round(shape.left!) - radius, Math.round(shape.top!) - radius, radius * 2, radius * 2, 0, 0, 1, 1);

        const data = this.colorPickerContext.getImageData(0, 0, 1, 1).data;

        // Use hex values because they are the easiest to deal with in color inputs
        const color = rgbValuesToHexString(data[0], data[1], data[2]);

        if (shape.fill !== color) {
            shape.set({
                fill: color,
            });
        }
    }

    isStickerOrMask(shape: fabric.Object | null): boolean {
        // Easiest solution to go by elimination since we keep track of every non-sticker object already
        // Shape can be null in getActiveObject. We might as well handle it here in case it can happen elsewhere too
        return !!shape && shape !== this.canvasImage && shape !== this.cropShape && shape !== this.cropShadow;
    }

    isMask(shape: fabric.Object): boolean {
        return shape !== this.canvasImage && shape !== this.cropShape && shape !== this.cropShadow && !(shape instanceof fabric.Image);
    }

    content() {
        const {original} = this.attrs.picture.imageUrls();

        const stickers: Sticker[] = app.forum.attribute('cimaiseStickers') || [];

        return m('.CimaiseStickerComposer-Body', [
            m('.CimaiseStickerComposer-Editor', [
                m('.CimaiseStickerComposer-DrawingBoard.js-sticker-editor', {
                    ondragover: (event: DragEvent) => {
                        if (event.dataTransfer!.getData('text/plain').indexOf('cimaise-sticker:') === 0) {
                            event.preventDefault();
                        }
                    },
                    ondrop: async (event: DragEvent) => {
                        const eventData = event.dataTransfer!.getData('text/plain');

                        if (eventData.indexOf('cimaise-sticker:') !== 0) {
                            return;
                        }

                        event.preventDefault();

                        const pointer = this.canvas.getPointer(event);

                        // Use circle options as type hint here since it also contains all the properties for rect and image
                        const options: fabric.ICircleOptions & ObjectCustomProperties = {
                            ...DEFAULT_OPTIONS,
                            left: pointer.x,
                            top: pointer.y,
                        };

                        let shape: fabric.Object | null = null;

                        switch (eventData) {
                            case 'cimaise-sticker:mask:rect':
                                options.fill = '#000';
                                options.width = Math.max(60 / this.canvas.getZoom(), 30); // Width of element in pick drawer, min width of the element (too small we can't find it again!)
                                options.height = Math.max(20 / this.canvas.getZoom(), 30); // TODO: apply ratio
                                shape = new fabric.Rect(options);
                                break;
                            case 'cimaise-sticker:mask:disc':
                                options.fill = '#000';
                                options.radius = Math.max(30 / this.canvas.getZoom(), 15);
                                options.lockRotation = true;
                                shape = new fabric.Circle(options);
                                break;
                            default:
                                const matches = /^cimaise-sticker:custom:(.+)$/.exec(eventData);

                                if (matches) {
                                    const stickersDefinitions: Sticker[] = app.forum.attribute('cimaiseStickers') || [];
                                    const stickerDefinition = stickersDefinitions.find(s => s.key === matches[1]);

                                    if (stickerDefinition) {
                                        options.stickerKey = matches[1];

                                        const image = await loadStickerImage(stickerDefinition.url);

                                        // Same logic as rects and discs. Target a given size but no smaller than another or you wouldn't find it again
                                        const targetWidth = Math.max(60 / this.canvas.getZoom(), 30);

                                        const scale = image.width > 0 ? (targetWidth / image.width) : 1;

                                        options.scaleX = scale;
                                        options.scaleY = scale;

                                        shape = new fabric.Image(image, options);
                                    }
                                }
                        }

                        if (!shape) {
                            throw 'Invalid Sticker value ' + eventData;
                        }

                        this.canvas.add(shape);
                        this.canvas.setActiveObject(shape);

                        this.updateShapeColor(shape);

                        this.dirty = true;
                    },
                    onmouseenter: () => {
                        this.canvas.getObjects().forEach(shape => {
                            if (this.isMask(shape)) {
                                shape.set({
                                    stroke: 'red',
                                });
                            }
                        });

                        this.canvas.requestRenderAll();
                    },
                    onmouseleave: () => {
                        this.canvas.getObjects().forEach(shape => {
                            if (this.isMask(shape)) {
                                shape.set({
                                    stroke: DEFAULT_OPTIONS.stroke,
                                });
                            }
                        });

                        this.canvas.requestRenderAll();
                    },
                }, m('.CimaiseStickerComposer-FloatingDrawingBoard', m('canvas', {
                    width: 500, // Will be updated by the resize observer
                    height: 800, // Will not change, but matches with the modal body min-height in CSS
                }))),
                m('.CimaiseStickerComposer-Status', [
                    original ? m('.CimaiseStickerComposer-Status-Item', 'Original ' + original[1] + 'x' + original[2]) : null,
                    this.cropShape ? m('.CimaiseStickerComposer-Status-Item', 'Cropped ' + this.cropShape.width + 'x' + this.cropShape.height) : null,
                    this.canvas ? m('.CimaiseStickerComposer-Status-Item', {
                        onclick: () => {
                            if (this.canvas.getZoom() === 1) {
                                this.zoomToFit();
                            } else {
                                this.canvas.setZoom(1);
                            }

                            // Reset position to top left
                            if (this.canvas.viewportTransform) {
                                this.canvas.viewportTransform[4] = 0;
                                this.canvas.viewportTransform[5] = 0;
                            }
                        },
                    }, [
                        'Zoom ',
                        m('span.js-sticker-editor-zoom', Math.round(this.canvas.getZoom() * 100) + '%'),
                    ]) : null,
                ]),
            ]),
            m('.CimaiseStickerComposer-List', [
                Button.component({
                    icon: 'fas fa-eraser',
                    className: 'Button',
                    onclick: () => {
                        if (!confirm('Delete all stickers, masks and crop?')) {
                            return;
                        }

                        // Remove all stickers
                        this.canvas.getObjects().forEach(object => {
                            if (this.isStickerOrMask(object)) {
                                this.canvas.remove(object);
                            }
                        });

                        const {original} = this.attrs.picture.imageUrls();

                        // Reset crop
                        if (original) {
                            this.cropShape.set({
                                top: 0,
                                left: 0,
                                width: original[1],
                                height: original[2],
                            });
                            this.resizeCropClipPath();
                            this.canvas.requestRenderAll();
                        }

                        this.dirty = true;
                    },
                }, 'Reset'),
                ' ',
                Button.component({
                    icon: 'fas fa-crop',
                    className: 'Button',
                    onclick: () => {
                        this.canvas.setActiveObject(this.cropShape);

                        const editor = this.element.querySelector('.js-sticker-editor')!;

                        // When zooming/panning to show the crop area, we want some additional space around for the edit handles
                        const CROP_VIEW_PADDING = 30;

                        const zoomToFit = (editor.getBoundingClientRect().width - CROP_VIEW_PADDING * 2) / this.cropShape.width!;

                        if (zoomToFit < 1) {
                            this.canvas.setZoom(zoomToFit);
                        }

                        this.canvas.absolutePan(new fabric.Point(this.cropShape.left! * zoomToFit - CROP_VIEW_PADDING, this.cropShape.top! * zoomToFit - CROP_VIEW_PADDING));
                    },
                }, 'Crop'),
                ' ',
                Button.component({
                    className: 'Button Button--primary',
                    loading: this.loading,
                    disabled: !this.dirty,
                    onclick: () => {
                        this.loading = true;
                        this.alertAttrs = null;

                        this.attrs.picture.save(this.data(), {
                            errorHandler: this.onerror.bind(this),
                        }).then(() => {
                            this.dirty = false;
                        }).catch(() => {
                        }).then(this.loaded.bind(this));
                    },
                }, 'Save'),
                m('h3', 'Mask'),
                m('p', 'Mask parts of the image for privacy. The original unaltered image remains accessible to you and moderators for future edits.'),
                m('.CimaiseStickerComposer-Insert', {
                    draggable: true,
                    ondragstart: (event: DragEvent) => {
                        event.dataTransfer!.setData('text/plain', 'cimaise-sticker:mask:rect');
                    },
                }, m('.CimaiseStickerComposer-InsertRect')),
                m('.CimaiseStickerComposer-Insert', {
                    draggable: true,
                    ondragstart: (event: DragEvent) => {
                        event.dataTransfer!.setData('text/plain', 'cimaise-sticker:mask:disc');
                    },
                }, m('.CimaiseStickerComposer-InsertDisc')),
                stickers.length > 0 ? [
                    m('h3', 'Stickers'),
                    m('p', 'These stickers are only to add information. The user can hide them when viewing the image.'),
                    stickers.map(sticker => m('.CimaiseStickerComposer-Insert', {
                        title: translate(sticker.label),
                        draggable: true,
                        ondragstart: (event: DragEvent) => {
                            event.dataTransfer!.setData('text/plain', 'cimaise-sticker:custom:' + sticker.key);
                        },
                    }, m('.CimaiseStickerComposer-InsertCustom', {
                        style: {
                            backgroundImage: 'url(' + sticker.url + ')',
                        },
                    }))),
                ] : null,
                m('h3', 'List'),
                m('p', 'Stickers already added to the image'),
                m('ul.CimaiseLayers', (this.canvas ? this.canvas.getObjects() as (fabric.Object & ObjectCustomProperties)[] : []).filter(this.isStickerOrMask.bind(this)).map((shape, index) => {
                    const stickerDefinition = stickers.find(s => s.key === shape.stickerKey);

                    return m('li', {
                        className: classList({
                            active: this.canvas.getActiveObject() === shape,
                            CimaiseLayerCircle: shape instanceof fabric.Circle,
                            CimaiseLayerRect: shape instanceof fabric.Rect,
                            CimaiseLayerSticker: shape instanceof fabric.Image,
                        }),
                        style: {
                            '--color': shape.fill,
                        },
                    }, [
                        m('span', '#' + (index + 1)),
                        shape instanceof fabric.Image ? m('span', stickerDefinition ? m('.CimaiseLayersStickerPreview', {
                            style: {
                                backgroundImage: 'url(' + stickerDefinition.url + ')',
                            },
                        }) : 'N/A' /* sticker deleted? */) : m('span', {
                            onclick() {
                                document.getElementById('cimaise-color-input-' + index)?.click();
                            },
                        }, m('.CimaiseLayersPreview')),
                        m('span', {
                            onclick: () => {
                                this.canvas.setActiveObject(shape);
                                this.canvas.requestRenderAll();
                            },
                        }, shape instanceof fabric.Image ? (stickerDefinition ? translate(stickerDefinition.label) : 'N/A') : 'Mask'),
                        shape instanceof fabric.Image ? null : m('input', {
                            id: 'cimaise-color-input-' + index,
                            type: 'color',
                            value: shape.fill,
                            onchange: (event: InputEvent) => {
                                shape.set({
                                    fill: (event.target as HTMLInputElement).value,
                                    isCustomColor: true,
                                });

                                this.dirty = true;
                            },
                        }),
                        Tooltip.component({
                            text: 'Remove',
                        }, Button.component({
                            icon: 'fas fa-times',
                            className: 'Button Button--icon',
                            onclick: () => {
                                this.canvas.remove(shape);

                                this.dirty = true;
                            },
                        })),
                    ]);
                })),
            ]),
        ]);
    }

    fromStickers(stickers: StickerPlacement[]): void {
        const stickersDefinitions: Sticker[] = app.forum.attribute('cimaiseStickers') || [];

        stickers.forEach(sticker => {
            const options: fabric.IObjectOptions = {
                ...DEFAULT_OPTIONS,
                left: sticker.left,
                top: sticker.top,
                angle: sticker.angle || 0,
            };

            if (sticker.type === 'mask:disc') {
                this.canvas.add(new fabric.Circle({
                    ...options,
                    fill: sticker.color || '#000',
                    lockRotation: true,
                    lockUniScaling: true,
                    radius: sticker.radius,
                }));
            } else if (sticker.type === 'mask:rect') {
                this.canvas.add(new fabric.Rect({
                    ...options,
                    fill: sticker.color || '#000',
                    width: sticker.width,
                    height: sticker.height,
                }));
            } else {
                const matches = /^custom:(.+)$/.exec(sticker.type);

                if (matches) {
                    const stickerDefinition = stickersDefinitions.find(s => s.key === matches[1]);

                    if (stickerDefinition) {
                        // Do this asynchronously since we need to load the images
                        // This might require an additional redraw for the layer list
                        (async () => {
                            const image = await loadStickerImage(stickerDefinition.url);

                            // Fallback width should not be too small or you would never find it again
                            const scale = image.width > 0 ? ((sticker.width || 30) / image.width) : 1;

                            this.canvas.add(new fabric.Image(image, {
                                ...options,
                                scaleX: scale,
                                scaleY: scale,
                                stickerKey: stickerDefinition.key,
                            } as fabric.IImageOptions & ObjectCustomProperties));
                        })();
                    }
                }
            }
        });
    }

    toStickers(): StickerPlacement[] {
        return this.canvas.getObjects().filter(this.isStickerOrMask.bind(this)).map(object => {
            const data: StickerPlacement = {
                type: '',
                left: object.left!,
                top: object.top!,
            };

            if (object instanceof fabric.Circle) {
                return {
                    ...data,
                    type: 'mask:disc',
                    color: object.fill as string,
                    radius: object.radius! * (object.scaleX || 1),
                };
            }

            if (object instanceof fabric.Rect) {
                return {
                    ...data,
                    type: 'mask:rect',
                    color: object.fill as string,
                    width: object.width! * (object.scaleX || 1),
                    height: object.height! * (object.scaleY || 1),
                    angle: object.angle,
                };
            }

            if (object instanceof fabric.Image) {
                // Use additional variable to be able to use custom property typings
                const stickerObject: fabric.Image & ObjectCustomProperties = object;

                if (stickerObject.stickerKey) {
                    return {
                        ...data,
                        type: 'custom:' + stickerObject.stickerKey,
                        width: object.width! * (object.scaleX || 1),
                        angle: object.angle,
                    };
                }
            }

            console.error(object);
            throw 'Invalid object';
        });
    }

    data() {
        return {
            stickers: this.toStickers(),
            crop: {
                top: this.cropShape.top,
                left: this.cropShape.left,
                width: this.cropShape.width,
                height: this.cropShape.height,
            },
        };
    }

    hide() {
        this.attrs.onhide?.();

        super.hide();
    }
}
